import type { LotteryRound } from '../../services/lotteryService'

/**
 * รองรับทั้ง key แบบ camelCase (threeUp, twoDown) และแบบ backend (LAST_2_DIGITS, 3_DIGIT_BOTTOM_*)
 */
export function getWinningNumbers(round: LotteryRound | null) {
  const wn = round?.results?.winningNumbers
  if (!wn) return { first: '', threeUp: '', twoDown: '', threeDown: '', fourUp: '', twoUp: '' }

  const first = wn.first ?? wn.FIRST_PRIZE ?? ''
  const twoDown = wn.twoDown ?? wn.LAST_2_DIGITS ?? ''
  const threeUp = wn.threeUp ?? wn['3_DIGIT_TOP'] ?? ''
  const threeDown =
    wn.threeDown ??
    wn['3_DIGIT_BOTTOM_FRONT_1'] ??
    wn['3_DIGIT_BOTTOM_BACK_1'] ??
    wn['3_DIGIT_BOTTOM_FRONT_2'] ??
    wn['3_DIGIT_BOTTOM_BACK_2'] ??
    ''

  return {
    first,
    threeUp: threeUp || (first.length >= 3 ? first.slice(0, 3) : ''),
    twoDown,
    threeDown,
    fourUp: wn.fourUp ?? wn['4_DIGIT_UP'] ?? '',
    twoUp: wn.twoUp ?? wn['2_DIGIT_UP'] ?? (first.length >= 2 ? first.slice(0, 2) : ''),
  }
}

export function hasAnyResult(round: LotteryRound | null): boolean {
  const w = getWinningNumbers(round)
  return !!(w.first || w.threeUp || w.twoDown || w.threeDown || w.fourUp || w.twoUp)
}

export function displayValue(
  value: string,
  options: { isCancelled?: boolean; hasResult?: boolean } = {}
): string {
  const { isCancelled = false, hasResult = true } = options
  if (isCancelled) return 'ยกเลิก'
  if (!hasResult || !value) return 'รอผล'
  return value
}

export function getDrawLabel(round: LotteryRound): string {
  return new Date(round.betEndTime).toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** งวด: DD-MMM-YYYY ใช้ timezone กรุงเทพฯ เพื่อไม่ให้วันกระโดด (เช่น 06 UTC → 07 ในไทย) */
export function getDrawLabelNgod(round: LotteryRound): string {
  const d = new Date(round.betEndTime)
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Bangkok',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  const parts = f.formatToParts(d)
  const day = parts.find((p) => p.type === 'day')?.value ?? String(d.getDate())
  const month = parts.find((p) => p.type === 'month')?.value ?? ''
  const year = parts.find((p) => p.type === 'year')?.value ?? String(d.getFullYear())
  return `งวด: ${day}-${month}-${year}`
}

const BANGKOK = 'Asia/Bangkok'

/** ได้วันที่ (YYYY-MM-DD) ในโซน Bangkok จาก ISO string */
function toDateStringBangkok(iso: string): string {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: BANGKOK, year: 'numeric', month: '2-digit', day: '2-digit' })
  const parts = f.formatToParts(new Date(iso))
  const y = parts.find((p) => p.type === 'year')!.value
  const m = parts.find((p) => p.type === 'month')!.value
  const d = parts.find((p) => p.type === 'day')!.value
  return `${y}-${m}-${d}`
}

/**
 * เช็คว่า selected date (YYYY-MM-DD) อยู่ในช่วง [betStart, betEnd] ของรอบหรือไม่ (เทียบวันใน Bangkok)
 * ใช้สำหรับ: betStart <= selected date <= betEnd → เอารอบที่เจอมาแสดง
 * หวยรัฐบาลไทย: แต่ละรอบมี betStartTime/betEndTime ตามกฎ (งวด 1: 20→1, งวด 16: 5→16)
 */
export function roundContainsDate(round: LotteryRound, dateStr: string): boolean {
  const startDate = toDateStringBangkok(round.betStartTime)
  const endDate = toDateStringBangkok(round.betEndTime)
  return dateStr >= startDate && dateStr <= endDate
}

/**
 * รอบแสดงเมื่อวันที่ที่เลือกตรงกับวันออกรางวัล (drawDate) หรืออยู่ในช่วงเปิด–ปิดรับแทง (betStart–betEnd)
 * ใช้หลักการเดียวกับหน้าประกาศผล (announce-results)
 */
export function isRoundVisibleOnDate(round: LotteryRound, dateStr: string): boolean {
  if (!dateStr?.trim()) return true
  const drawDate = round.drawDate ? toDateStringBangkok(round.drawDate) : toDateStringBangkok(round.betEndTime)
  if (drawDate === dateStr) return true
  if (roundContainsDate(round, dateStr)) return true
  return false
}

/** วันออกรางวัลของรอบ (YYYY-MM-DD ใน Bangkok) — ใช้กรองไม่เอารอบของวันอื่น (เช่น เมื่อวาน) */
export function getRoundDrawDateString(round: LotteryRound): string {
  return round.drawDate ? toDateStringBangkok(round.drawDate) : toDateStringBangkok(round.betEndTime)
}

/** true ถ้าวันออกรางวัลของรอบตรงกับ dateStr เท่านั้น (ไม่เอารอบที่แค่ overlap ช่วงรับแทง) */
export function isRoundDrawDateExactly(round: LotteryRound, dateStr: string): boolean {
  if (!dateStr?.trim()) return true
  return getRoundDrawDateString(round) === dateStr
}

/**
 * ดึงวันที่จาก roundNumber รูปแบบ LYK-YYYYMMDD-NNN (อักษรย่อ-วันที่-รอบที่)
 * คืน YYYY-MM-DD หรือ null ถ้าไม่ match
 */
export function getDateStringFromRoundNumber(roundNumber: string | undefined): string | null {
  if (!roundNumber?.trim()) return null
  const m = roundNumber.match(/^[^-]+-(\d{8})-/)
  if (!m) return null
  const y = m[1].slice(0, 4)
  const mo = m[1].slice(4, 6)
  const d = m[1].slice(6, 8)
  return `${y}-${mo}-${d}`
}

/** true ถ้าวันที่ใน roundNumber (LYK-YYYYMMDD-NNN) ตรงกับ dateStr — ใช้กรองหวยยี่กีไม่เอารอบของวันอื่น */
export function isRoundNumberDateExactly(round: LotteryRound, dateStr: string): boolean {
  if (!dateStr?.trim()) return true
  const fromRoundNumber = getDateStringFromRoundNumber(round.roundNumber)
  if (fromRoundNumber != null) return fromRoundNumber === dateStr
  return getRoundDrawDateString(round) === dateStr
}
